import type { ComputedRef } from 'vue';
import { computeAverageScore, formatScorePercent, getScoreSeverity } from '~/composables/opportunities/analysis/bank-account-statements/statementScoreUtils';

// Score Analyse du dossier (moyenne des statuts de relevés) : fetch + calcul partagés par les
// onglets desktop (LayoutV2SubTabs) et le shell mobile (MobileApp). `severity` est renvoyée brute
// (success | warn | …) ; chaque appelant la mappe sur son propre jeu de tons.
// Fetch suivi sur le hashId de route (immédiat) : MobileApp est monté une fois au niveau layout,
// un onMounted resterait bloqué si le dossier n'est pas encore résolu et ne se rejouerait pas au
// changement de dossier.
// `hasScore` porte la règle d'affichage du badge : pas de relevé noté (`averageScore` à null) ou
// score qui s'affiche à 0 une fois tronqué en pourcentage.
export function useAnalyseScore (): {
  averageScore: ComputedRef<number | null>;
  scorePercent: ComputedRef<string | null>;
  hasScore: ComputedRef<boolean>;
  severity: ComputedRef<ReturnType<typeof getScoreSeverity>>;
} {
  const route = useRoute();
  const store = useOpportunityStatementsStatusesStore();
  const { opportunityStatementsStatuses } = storeToRefs(store);

  const averageScore = computed<number | null>(() => computeAverageScore(opportunityStatementsStatuses.value ?? []));
  const scorePercent = computed<string | null>(() => formatScorePercent(averageScore.value));
  // Le garde porte sur la valeur rendue, pas sur la moyenne : `formatScorePercent` tronque, donc
  // une moyenne strictement positive peut afficher « 0 ».
  const hasScore = computed<boolean>(() => scorePercent.value !== null && scorePercent.value !== '0');
  const severity = computed(() => getScoreSeverity(averageScore.value));

  watch(() => route.params.opportunityHashId, (hashId) => {
    if (typeof hashId === 'string' && hashId) { void store.fetchOpportunityStatementsStatuses(hashId); }
  }, { immediate: true });

  return { averageScore, scorePercent, hasScore, severity };
}
